import * as React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useHref } from 'react-router-dom';
import { SocialIcon } from './ui/SocialIcon';
import type { SocialIconName } from './ui/SocialIcon';
import { Wordmark, BRAND_NAME } from './ui/Wordmark';
import { LanguageToggle } from './ui/LanguageToggle';
import { useContent, text, link } from '../hooks/useContent';
import { Button } from './ui/Button';
import { PromoModal } from './ui/PromoModal';

type NavLink = {
  /** CMS text key for the label; falls back to `label` when absent. */
  labelKey?: string;
  label?: string;
  href: string;
};

type Props = {
  scrollEl?: HTMLElement | null;
  activePage?: string;
  onNavigate?: (page: string) => void;
  forceElevated?: boolean;
};

/** Cmd/Ctrl/Shift/Alt-clicks and middle-clicks must keep native anchor
 *  behavior (open in new tab/window) — never hijack them into SPA routing. */
const isPlainLeftClick = (e: React.MouseEvent) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const primaryLinks: NavLink[] = [
  { labelKey: 'nav.home', href: '/' },
  { labelKey: 'nav.decks', href: '/decks' },
  { labelKey: 'nav.radio', href: '/spirit-box' },
  { labelKey: 'nav.reading', href: '/reading' },
  { labelKey: 'nav.dailyCard', href: '/card-of-the-day' },
  { labelKey: 'nav.reviews', href: '/reviews' },
  // Fullscreen-menu only (see the desktop-row filter below).
  { labelKey: 'nav.subscribe', href: '/subscribe' },
  // Dev-only link, never user-facing copy — kept as a literal label.
  ...(process.env.NODE_ENV === 'development' ? [{ label: 'Design', href: '/design-system' }] : []),
];

export default function Navbar({ scrollEl, activePage, onNavigate, forceElevated = false }: Props) {
  const { data: content } = useContent();
  const navLabel = (l: NavLink) => (l.labelKey ? text(content, l.labelKey) : (l.label ?? ''));

  const allSocialLinks: { label: SocialIconName; href: string }[] = [
    { label: 'YouTube', href: link(content, 'social.youtube') },
    { label: 'TikTok', href: link(content, 'social.tiktok') },
    { label: 'Instagram', href: link(content, 'social.instagram') },
    { label: 'Facebook', href: link(content, 'social.facebook') },
    { label: 'X', href: link(content, 'social.x') },
    { label: 'Discord', href: link(content, 'social.discord') },
    { label: 'VKVideo', href: link(content, 'social.vkvideo') },
    { label: 'Telegram', href: link(content, 'social.telegram') },
    { label: 'Email', href: link(content, 'social.email') },
  ];
  // A social link with no URL configured in the CMS is hidden, not rendered empty.
  const socialLinks = allSocialLinks.filter((l) => l.href);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Router basename (PUBLIC_URL on gh-pages) so raw hrefs stay correct when
  // opened directly (new tab, copy link).
  const baseHref = useHref('/');
  const toHref = (href: string) => (href === '/' ? baseHref : baseHref.replace(/\/$/, '') + href);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const promo = content?.promoModal ?? null;
  const [isPromoOpen, setIsPromoOpen] = React.useState(false);
  const autoPromoTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!promo || !promo.autoOpen) return;
    const key = `promo-seen:${promo.id}:${promo.updatedAt}`;
    if (window.localStorage.getItem(key)) return;
    // The "seen" key is written when the modal actually opens, not here — a
    // visitor who leaves before the delay elapses must keep their one showing.
    const timer = window.setTimeout(() => {
      autoPromoTimerRef.current = null;
      window.localStorage.setItem(key, '1');
      setIsPromoOpen(true);
    }, promo.autoOpenDelay * 1000);
    autoPromoTimerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      autoPromoTimerRef.current = null;
    };
  }, [promo]);

  const openPromo = () => {
    // Opening by hand consumes the pending auto-open, so the modal cannot
    // re-pop on top of the one the visitor just closed.
    if (autoPromoTimerRef.current !== null) {
      window.clearTimeout(autoPromoTimerRef.current);
      autoPromoTimerRef.current = null;
    }
    setIsMenuOpen(false);
    setIsPromoOpen(true);
  };

  const [isElevated, setIsElevated] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const hideTimerRef = React.useRef<number | null>(null);
  const isHoveredRef = React.useRef(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const hamburgerTop =
    'absolute left-0 top-0 h-[1px] w-full bg-current transition-transform duration-200';
  const hamburgerMid =
    'absolute left-0 top-[4px] h-[1px] w-full bg-current transition-opacity duration-200';
  const hamburgerBot =
    'absolute left-0 top-[8px] h-[1px] w-full bg-current transition-transform duration-200';

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  React.useEffect(() => {
    const prev = document.body.style.overflow;
    if (isMenuOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  // Dialog focus management: move focus into the menu on open, trap Tab
  // inside it, and return focus to the trigger on close.
  React.useEffect(() => {
    if (!isMenuOpen) return;
    const menu = menuRef.current;
    if (!menu) return;
    const trigger = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(menu.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
    focusables()[0]?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    menu.addEventListener('keydown', onKeyDown);
    return () => {
      menu.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (forceElevated) return;

    let raf = 0;

    const target: HTMLElement | Window = scrollEl ?? window;
    const readScrollY = () =>
      target instanceof HTMLElement
        ? target.scrollTop || 0
        : window.scrollY || document.documentElement.scrollTop || 0;

    const update = () => {
      raf = 0;
      const y = readScrollY();
      const atTop = y <= 8;
      setIsElevated(y > 8);
      if (atTop || isMenuOpen) setIsVisible(true);
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);

      setIsVisible(true);

      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        const y = readScrollY();
        const atTop = y <= 8;
        if (!atTop && !isMenuOpen && !isHoveredRef.current) setIsVisible(false);
      }, 900);
    };

    update();
    target.addEventListener('scroll', onScroll, { passive: true } as any);

    return () => {
      target.removeEventListener('scroll', onScroll as any);
      if (raf) window.cancelAnimationFrame(raf);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [scrollEl, isMenuOpen, forceElevated]);

  // /spirit-box is nocturnal on every breakpoint, so the bar's brand/links/icons
  // go white over its dark dusk — but only in the transparent state: once
  // elevated, the white pill returns and the default dark type reads
  // against it.
  const onDarkPage = activePage === 'radio';
  const lightOnDark = onDarkPage && !(isElevated || forceElevated);

  return (
    <>
      <header
        onMouseEnter={() => {
          isHoveredRef.current = true;
          setIsVisible(true);
        }}
        onMouseLeave={() => {
          isHoveredRef.current = false;
        }}
        className={[
          'fixed inset-x-0 top-0 z-50 transition-opacity duration-1000 ease-out motion-reduce:transition-none',
          isVisible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <div className="t-container my-4">
          <div
            className={[
              'flex items-center gap-4 px-5 py-3',
              'transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-out motion-reduce:transition-none',
              isElevated || forceElevated
                ? 'rounded-[14px] border border-[rgba(23,23,23,0.06)] bg-white/90 shadow-[0_6px_20px_rgba(26,53,80,0.10)] backdrop-blur-[10px]'
                : 'rounded-[14px] border border-transparent bg-white/0 shadow-none backdrop-blur-0',
              lightOnDark ? 'nav--on-dark' : '',
            ].join(' ')}
          >
            {/* The lockup (SVG lines, max-width:100%) scales down when the row
                runs out of room. RU labels + the promo CTA overshoot the capped
                t-container by ~40px, so the lockup must stay shrinkable at
                every width — a shrink-0 here pushes the CTA out of the pill. */}
            <div className="flex items-center gap-4">
              <a
                href={toHref('/')}
                aria-label={text(content, 'nav.home')}
                className="flex flex-col gap-1.5 cursor-pointer"
                onClick={(e) => {
                  if (!isPlainLeftClick(e)) return;
                  e.preventDefault();
                  const onDecksPage = activePage === 'decks' || pathname === '/decks';
                  if (onDecksPage) onNavigate?.('main');
                  else if (pathname !== '/') navigate('/');
                  else scrollEl?.scrollTo({ top: 0, behavior: 'smooth' });
                  if (window.location.hash) {
                    window.history.replaceState(
                      null,
                      '',
                      window.location.pathname + window.location.search,
                    );
                  }
                }}
              >
                <Wordmark
                  size="nav"
                  gapSize="sm"
                  showMark={false}
                  inverse={lightOnDark}
                  taglineTitle={text(content, 'brand.tagline') || undefined}
                />
              </a>
            </div>

            {/* Desktop nav — from navrow (1152px, the t-container cap) only:
                below it the container narrows with the viewport and the full
                row (wordmark + links + EN/RU + promo CTA) cannot fit — the
                lockup would collapse to nothing — so tablets and small
                laptops get the fullscreen menu like mobile. */}
            <nav className="hidden flex-1 items-center justify-end navrow:flex">
              <ul className="flex items-center gap-[18px]">
                {/* The dev-only Design link and the Subscribe link don't fit
                    the capped t-container next to the unshrunk lockup —
                    desktop row drops them (still reachable via the fullscreen
                    menu and direct URL). */}
                {primaryLinks
                  .filter((l) => l.href !== '/design-system' && l.href !== '/subscribe')
                  .map((l) => {
                    const isHomeLink = l.href === '/';
                    const isDecksLink = l.href === '/decks';
                    const isRadioLink = l.href === '/spirit-box';
                    const isReadingLink = l.href === '/reading';
                    const isDailyLink = l.href === '/card-of-the-day';
                    const isDesignLink = l.href === '/design-system';
                    const isReviewsLink = l.href === '/reviews';
                    const onDecksPage = activePage === 'decks' || pathname === '/decks';
                    const onRadioPage = activePage === 'radio' || pathname === '/spirit-box';
                    const isActive = isDecksLink
                      ? onDecksPage
                      : isRadioLink
                        ? onRadioPage
                        : isReadingLink
                          ? pathname === '/reading'
                          : isDailyLink
                            ? pathname === '/card-of-the-day'
                            : isDesignLink
                              ? pathname === '/design-system'
                              : isReviewsLink
                                ? pathname === '/reviews'
                                : pathname === '/';

                    const handleClick = (e: React.MouseEvent) => {
                      if (!isPlainLeftClick(e)) return;
                      if (isReadingLink) {
                        e.preventDefault();
                        navigate('/reading');
                      } else if (isDailyLink) {
                        e.preventDefault();
                        navigate('/card-of-the-day');
                      } else if (isDesignLink) {
                        e.preventDefault();
                        navigate('/design-system');
                      } else if (isReviewsLink) {
                        e.preventDefault();
                        navigate('/reviews');
                      } else if (isDecksLink && !onDecksPage) {
                        e.preventDefault();
                        if (onNavigate) onNavigate('decks');
                        else navigate('/decks');
                      } else if (isRadioLink && !onRadioPage) {
                        e.preventDefault();
                        if (onNavigate) onNavigate('radio');
                        else navigate('/spirit-box');
                      } else if (isHomeLink) {
                        e.preventDefault();
                        if (onDecksPage || onRadioPage) onNavigate?.('main');
                        else if (pathname !== '/') navigate('/');
                        else scrollEl?.scrollTo({ top: 0, behavior: 'smooth' });
                        if (window.location.hash) {
                          window.history.replaceState(
                            null,
                            '',
                            window.location.pathname + window.location.search,
                          );
                        }
                      }
                    };

                    return (
                      <li
                        key={l.href}
                        className={`h-full flex items-center whitespace-nowrap navbar-link ${isActive ? 'active' : ''}`}
                      >
                        <a
                          href={toHref(l.href)}
                          onClick={handleClick}
                          className="t-nav-link transition"
                          data-label={navLabel(l)}
                        >
                          {navLabel(l)}
                        </a>
                      </li>
                    );
                  })}
              </ul>

              <div
                className={`flex items-center gap-[9px] ml-[14px] pl-[14px] border-l ${
                  lightOnDark ? 'border-white/25' : 'border-[color:var(--primary-15)]'
                }`}
              >
                <LanguageToggle tone={lightOnDark ? 'onDark' : 'default'} />
              </div>
              {promo ? (
                <div className="flex items-center pl-[14px]">
                  <Button variant="navcta" onClick={openPromo}>
                    {promo.triggerLabel}
                  </Button>
                </div>
              ) : null}
            </nav>

            {/* Mobile menu button */}
            <button
              type="button"
              className="t-menu-trigger group ml-auto inline-flex items-center gap-2 navrow:hidden"
              aria-haspopup="dialog"
              aria-expanded={isMenuOpen}
              aria-controls="brand-menu"
              onClick={() => setIsMenuOpen((v) => !v)}
            >
              <span>{text(content, 'nav.menu')}</span>
              <span aria-hidden="true" className="relative inline-block h-[10px] w-[14px]">
                <span
                  className={[hamburgerTop, isMenuOpen ? 'translate-y-[4px] rotate-45' : ''].join(
                    ' ',
                  )}
                />
                <span className={[hamburgerMid, isMenuOpen ? 'opacity-0' : ''].join(' ')} />
                <span
                  className={[hamburgerBot, isMenuOpen ? '-translate-y-[4px] -rotate-45' : ''].join(
                    ' ',
                  )}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Portaled to body: ancestors animate opacity (.route-fade) and thus
          form stacking contexts that would trap the overlay's z-index below
          body-level floating controls (MuteToggle) — same fix as the splash. */}
      {isMenuOpen ? createPortal(
        <div
          id="brand-menu"
          ref={menuRef}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] overflow-y-auto bg-white"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsMenuOpen(false);
          }}
        >
          <div className="t-container flex h-full flex-col py-6">
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setIsMenuOpen(false)}>
                <Wordmark
                  size="nav"
                  gapSize="sm"
                  showMark={false}
                  taglineTitle={text(content, 'brand.tagline') || undefined}
                />
              </button>
              <button type="button" className="t-menu-trigger" onClick={() => setIsMenuOpen(false)}>
                {text(content, 'nav.close')}
              </button>
            </div>

            <nav className="mt-12 grid gap-12 md:grid-cols-2">
              <div>
                <div className="t-eyebrow-blue">{text(content, 'nav.menu')}</div>
                <ul className="mt-6 space-y-5">
                  {primaryLinks.map((l) => {
                    const isHomeLink = l.href === '/';
                    const isDecksLink = l.href === '/decks';
                    const isRadioLink = l.href === '/spirit-box';
                    const isReadingLink = l.href === '/reading';
                    const isDailyLink = l.href === '/card-of-the-day';
                    const isDesignLink = l.href === '/design-system';
                    const isReviewsLink = l.href === '/reviews';
                    const isSubscribeLink = l.href === '/subscribe';
                    const onDecksPage = activePage === 'decks';
                    const onRadioPage = activePage === 'radio';

                    const handleClick = (e: React.MouseEvent) => {
                      if (!isPlainLeftClick(e)) return;
                      setIsMenuOpen(false);
                      if (isReadingLink) {
                        e.preventDefault();
                        navigate('/reading');
                      } else if (isDailyLink) {
                        e.preventDefault();
                        navigate('/card-of-the-day');
                      } else if (isDesignLink) {
                        e.preventDefault();
                        navigate('/design-system');
                      } else if (isReviewsLink) {
                        e.preventDefault();
                        navigate('/reviews');
                      } else if (isSubscribeLink) {
                        e.preventDefault();
                        navigate('/subscribe');
                      } else if (isDecksLink && !onDecksPage) {
                        e.preventDefault();
                        if (onNavigate) onNavigate('decks');
                        else navigate('/decks');
                      } else if (isRadioLink && !onRadioPage) {
                        e.preventDefault();
                        if (onNavigate) onNavigate('radio');
                        else navigate('/spirit-box');
                      } else if (isHomeLink) {
                        e.preventDefault();
                        if (onDecksPage || onRadioPage) onNavigate?.('main');
                        else if (pathname !== '/') navigate('/');
                        else scrollEl?.scrollTo({ top: 0, behavior: 'smooth' });
                        if (window.location.hash) {
                          window.history.replaceState(
                            null,
                            '',
                            window.location.pathname + window.location.search,
                          );
                        }
                      }
                    };

                    return (
                      <li key={l.href}>
                        <a
                          href={toHref(l.href)}
                          className="t-menu-link block transition hover:opacity-70"
                          onClick={handleClick}
                        >
                          {navLabel(l)}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                {promo ? (
                  <div className="mt-8">
                    <Button variant="solid" onClick={openPromo} fluid>
                      {promo.triggerLabel}
                    </Button>
                  </div>
                ) : null}
              </div>

              <div>
                <div className="t-eyebrow-blue">{text(content, 'nav.follow')}</div>
                <ul className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                  {socialLinks.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        {...(l.href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                        className="inline-flex items-center justify-center text-[26px] text-primary transition hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                        onClick={() => setIsMenuOpen(false)}
                        aria-label={l.label}
                      >
                        <span className="sr-only">{l.label}</span>
                        <SocialIcon name={l.label} />
                      </a>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <LanguageToggle />
                </div>
              </div>
            </nav>

            <div className="mt-auto pt-10 t-eyebrow">
              <span>© {new Date().getFullYear()} {text(content, 'brand.name') || BRAND_NAME}</span>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
      <PromoModal promo={promo} open={isPromoOpen} onClose={() => setIsPromoOpen(false)} />
    </>
  );
}
